import { api, remove, send } from './client';
import { ME_PATH, type Me } from './meta';
import { query, type Cursor, type CursorPage, type OffsetMeta, type OffsetPage } from './paging';

/** GET/PATCH /auth/me: the session user plus what the account page shows. */
export interface Profile extends Me {
  createdAt: string;
  lastLoginAt: string | null;
  /** when the password was last set (changed, reset by an admin, account created); absent on an older backend */
  passwordChangedAt?: string;
}

/** One device signed in to the caller's account. `id` is an opaque handle, not the session secret. */
export interface AccountSession {
  id: string;
  createdAt: string;
  expiresAt: string;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
}
export type AccountSessionPage = OffsetPage<AccountSession>;

/** How many other devices a password change or "sign out the others" signed out. */
export interface SignedOut { signedOut: number }

/**
 * How a sign-in attempt of the caller's account ended. `success` (password, then the code when asked), or why it
 * failed: the reasons of the backend's audit `login.failed` (a reason this dashboard does not know shows as a plain
 * failure).
 */
export type SignInResult = 'success' | 'wrong_password' | 'totp_invalid' | 'rate_limited' | 'inactive' | 'client_outdated' | 'unknown_email' | (string & {});
/** One sign-in attempt (GET /auth/sign-ins): when, from where, how it ended, and whether a recovery code was used. */
export interface SignIn {
  id: string | number;
  at: string;
  result: SignInResult;
  ip: string | null;
  userAgent: string | null;
  /** how a successful sign-in was completed; null for a failure */
  method?: 'password' | 'totp' | 'recovery_code' | 'totp_enroll' | null;
}
export type SignInPage = CursorPage<SignIn, string | number>;

/** GET /auth/totp: the caller's two-step verification. */
export interface TotpState {
  enabled: boolean;
  enrolledAt: string | null;
  recoveryCodesLeft: number;
  /** the policy requires it for this account (it cannot be turned off) */
  required?: boolean;
}
/** POST /auth/totp/setup: the new secret (base32) and its otpauth:// URI for the QR; never sent again after enabling. */
export interface TotpSetup { secret: string; otpauthUri: string }
/** POST /auth/totp/enable and /auth/totp/recovery-codes: one-time recovery codes, shown once. */
export interface RecoveryCodes { recoveryCodes: string[] }

export const PROFILE_PATH = ME_PATH;
export const SESSIONS_PATH = '/auth/sessions';
export const TOTP_PATH = '/auth/totp';

export const accountPaths = {
  sessions: (page: number, limit: number) => `${SESSIONS_PATH}${query({ page, limit })}`,
  signIns: (cursor: Cursor<string | number>, limit: number) => `/auth/sign-ins${query({ ...cursor, limit })}`,
  totp: TOTP_PATH,
};

/**
 * The sessions as a page whatever the backend answers: a page (`{ items, meta }`), or the whole list of an older
 * backend (sliced here, so the card and the drawer behave the same).
 */
export function sessionPage(raw: AccountSessionPage | AccountSession[], page: number, limit: number): AccountSessionPage {
  if (!Array.isArray(raw)) return raw;
  const total = raw.length;
  // this device first, as the paged answer orders them
  const rows = [...raw].sort((a, b) => Number(b.current) - Number(a.current));
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const meta: OffsetMeta = { page, limit, total, totalPages, hasNext: page < totalPages, hasPrev: page > 1 };
  return { items: rows.slice((page - 1) * limit, page * limit), meta };
}

export const renameMe = (name: string) => send<Profile>(ME_PATH, { name }, 'PATCH');
export const changePassword = (current: string, next: string) => api<SignedOut>('/auth/password', { body: { current, next } });
export const signOutOtherSessions = () => send<SignedOut>(`${SESSIONS_PATH}/sign-out-others`);
export const signOutSession = (id: string) => remove(`${SESSIONS_PATH}/${encodeURIComponent(id)}`);

/** Works with the pending sign-in of an account that must enroll, or a full session. */
export const setupTotp = () => send<TotpSetup>(`${TOTP_PATH}/setup`);
/**
 * The first code from the app turns it on; the answer carries the recovery codes. Signed in, the password is needed
 * too (the answer says how many other devices it signed out); at the sign-in's enrollment step it is not, and the
 * answer carries the user the server just signed in.
 */
export const enableTotp = (code: string, password?: string) => send<RecoveryCodes & { me?: Me; signedOut?: number }>(`${TOTP_PATH}/enable`, password === undefined ? { code } : { code, password });
/** Turning it off takes the password and a code from the app (or a recovery code); refused when the policy requires it. */
export const disableTotp = (password: string, code: string) => send<void>(`${TOTP_PATH}/disable`, { password, code });
export const regenerateRecoveryCodes = (code: string) => send<RecoveryCodes>(`${TOTP_PATH}/recovery-codes`, { code });
